<script setup lang="ts">
import { GpBtn, GpTooltip } from '@gportal/uikit';
import LayoutOrderSummary from '@panel/layouts/order/Layout.Order.Summary.vue';
import type { ServiceError } from 'gpcore-apikit/grpc/types';
import { type Authentication, useNodeStore } from 'gpcore-apikit/store/pinia/node';
import { AuthenticationType, type BasicImage } from 'gpcore-grpc/gpcore/api/cloud/v1/image_pb';
import type { Node } from 'gpcore-grpc/gpcore/api/cloud/v1/node_pb';
import { computed, ref, useAttrs } from 'vue';
import { useRouter } from 'vue-router';
import ReinstallConfirmModal from './ReinstallConfirmModal.vue';
import useNotifications from 'gpcore-uikit/composables/useNotifications';

const props = defineProps<{
  sectionsMapping: Record<
    string,
    {
      label: string;
      isVisible: boolean;
      isMounted: boolean;
      isLoading?: boolean;
      isDone?: boolean;
      isInvalid?: boolean;
    }
  >;
  node: Node;
  reinstall: {
    id: string;
    fqdn?: string;
    image?: BasicImage;
    authentication?: Authentication;
    settings?: {
      userData: string;
    };
  };
}>();

const disableReason = computed(() => {
  if (!props.reinstall.image) {
    return 'No image selected.';
  }

  if (props.reinstall.authentication == null) {
    return 'No authentication method selected.';
  }

  if (
    props.reinstall.authentication.type === AuthenticationType.PASSWORD &&
    (!props.reinstall.authentication.password ||
      props.reinstall.authentication.password.length === 0)
  ) {
    return 'Please enter a password.';
  }

  if (
    props.reinstall.authentication.type === AuthenticationType.SSH &&
    props.reinstall.authentication.sshKeys?.length === 0
  ) {
    return 'Please select SSH public keys.';
  }

  return null;
});

const nodeStore = useNodeStore();
const router = useRouter();
const { addNotification } = useNotifications();
const confirmModalOpened = ref(false);
const isLoading = ref(false);
const doReinstall = () => {
  if (disableReason.value == null) {
    isLoading.value = true;
    nodeStore
      .reinstallNode(props.reinstall)
      .then(() => {
        router.push({ name: 'ProjectNodeOverview' });
        addNotification({
          message: 'The node will be reinstalled now.',
          type: 'success',
        });
      })
      .catch((error: ServiceError) => {
        addNotification({
          message: error.message,
          type: 'danger',
        });
      })
      .finally(() => {
        isLoading.value = false;
        confirmModalOpened.value = false;
      });
  }
};

const attrs = useAttrs();
</script>

<template>
  <LayoutOrderSummary
    :sections-mapping="sectionsMapping"
    title="Reinstall node"
    class="order:tw-max-h-[17rem] tw-mt-8"
    :class="attrs.class"
  >
    <template #footer>
      <GpTooltip
        :content="disableReason"
        class="tw-w-full"
        trigger-class="tw-w-full"
        placement="top"
      >
        <GpBtn
          class="tw-mt-4 tw-w-full"
          :icon="['fas', 'fa-undo']"
          label="Reinstall"
          :disabled="disableReason != null"
          @click="confirmModalOpened = true"
        />
      </GpTooltip>
    </template>
  </LayoutOrderSummary>
  <ReinstallConfirmModal
    :show="confirmModalOpened"
    :fqdn-to-confirm="reinstall.fqdn"
    :is-loading="isLoading"
    @confirm="doReinstall"
    @close="confirmModalOpened = false"
  />
</template>
